// "Open AiGentPanel" (ADI-02; ADR-0018 §2): an agency operator asks the API for a one-time, 60 s
// launch link (`POST /v1/operator/aigentpanel/launch`, which records the token) and the app opens
// it in a new tab. The tab is opened synchronously on the click (so no popup blocker stops it),
// detached from this window (`opener = null`), and only then pointed at the link; the link is
// never stored or shown. On a refusal the blank tab is closed and the problem is toasted by the
// app's mutation error handler. The panel itself verifies the token and starts its own session.
import { Button, Tooltip, TooltipContent, TooltipTrigger } from "@companyos/ui";
import { useMutation } from "@tanstack/react-query";
import { ExternalLinkIcon } from "lucide-react";
import type { Api } from "../../api/client";

type Target = { companyId: string; deId?: string };

export function useOpenAigentpanel(api: Api) {
  const launch = useMutation({
    mutationFn: async ({ target, tab }: { target: Target; tab: Window | null }) => {
      try {
        const r = await api.client.operator.aigentpanel.launch({
          body: {
            company_id: target.companyId,
            ...(target.deId ? { de_membership_id: target.deId } : {}),
          },
        });
        if (tab) tab.location.replace(r.url);
        else window.location.assign(r.url);
        return r;
      } catch (e) {
        tab?.close();
        throw e;
      }
    },
  });
  return {
    pending: launch.isPending,
    open(target: Target) {
      const tab = window.open("", "_blank");
      if (tab) tab.opener = null;
      launch.mutate({ target, tab });
    },
  };
}

/** The labelled button (DE profile header). */
export function OpenAigentpanelButton({
  api,
  companyId,
  deId,
}: {
  api: Api;
  companyId: string;
  deId?: string;
}) {
  const panel = useOpenAigentpanel(api);
  return (
    <Button
      variant="secondary"
      disabled={panel.pending}
      onClick={() => panel.open({ companyId, ...(deId ? { deId } : {}) })}
      data-slot="open-aigentpanel"
    >
      <ExternalLinkIcon aria-hidden="true" />
      Open AiGentPanel
    </Button>
  );
}

/** The icon button of a company row (operator console). */
export function OpenAigentpanelIconButton({
  api,
  companyId,
  companyName,
}: {
  api: Api;
  companyId: string;
  companyName: string;
}) {
  const panel = useOpenAigentpanel(api);
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Open AiGentPanel for ${companyName}`}
            disabled={panel.pending}
            onClick={() => panel.open({ companyId })}
          />
        }
      >
        <ExternalLinkIcon aria-hidden="true" />
      </TooltipTrigger>
      <TooltipContent>Open AiGentPanel</TooltipContent>
    </Tooltip>
  );
}
